import type { Product, SupplierPriceBookEntry } from '../data/types';
import {
  autoMatchInternalCode, classifyImportLine, mintInternalCode, guessLineType,
  type ImportLineState, type ImportUserAction,
} from './codeMatching';

export interface ParsedImportLine {
  supplierCode?: string;
  description: string;
  qty?: number;
  unitPrice?: number;
}

export interface ImportPreviewDeps {
  supplierId: string;
  supplierName: string;
  products: Product[];
  bookForSupplier: SupplierPriceBookEntry[];
  lookupSupplierCode: (supplierCode: string) => string | undefined;
  /** Company 3-letter prefix for minting (FIE-234). */
  internalCodePrefix?: string;
  addProduct: (draft: {
    code: string;
    description: string;
    category: string;
    primarySupplier: string;
    purchasePrice: number;
    markup: number;
    stock: number;
    minStock: number;
  }) => void;
}

export function classifyRawImportLine(
  line: ParsedImportLine,
  deps: ImportPreviewDeps,
): ImportLineState {
  const sc = (line.supplierCode || '').trim();
  const entry = sc
    ? deps.bookForSupplier.find(e => e.supplierCode.toUpperCase() === sc.toUpperCase())
    : undefined;
  return classifyImportLine({
    supplierCode: sc,
    description: line.description,
    supplierName: deps.supplierName,
    products: deps.products,
    priceBookEntry: entry,
    remembered: sc ? deps.lookupSupplierCode(sc) : undefined,
  });
}

export function buildImportLineResolution(
  line: ParsedImportLine,
  deps: ImportPreviewDeps,
): ImportLineState {
  const base = classifyRawImportLine(line, deps);
  if (base.status === 'new' && guessLineType(line) === 'service') {
    return { ...base, status: 'service', userAction: 'service' };
  }
  return base;
}

export function buildInitialImportResolutions(
  lines: ParsedImportLine[],
  deps: ImportPreviewDeps,
): Record<number, ImportLineState> {
  const resolutions: Record<number, ImportLineState> = {};
  lines.forEach((l, i) => { resolutions[i] = buildImportLineResolution(l, deps); });
  return resolutions;
}

export function resolveImportPreviewInternal(
  line: ParsedImportLine,
  res: ImportLineState,
  deps: ImportPreviewDeps,
  /** Mutated — tracks PREFIX-N codes minted in this apply pass (uniqueness). */
  mintedInBatch: string[] = [],
): { internalCode: string; lineType: 'product' | 'service' } {
  if (res.status === 'service' || res.userAction === 'service') {
    return { internalCode: '', lineType: 'service' };
  }
  if (res.status === 'matched' && res.internalCode) {
    return { internalCode: res.internalCode, lineType: 'product' };
  }
  if (res.userAction === 'confirmSuggested' && res.suggestedInternal) {
    return { internalCode: res.suggestedInternal, lineType: 'product' };
  }
  if (res.userAction === 'autoCreate') {
    // Supplier-agnostic mint — never embeds supplier code/name.
    // Include codes already minted in this batch so 9 lines → 9 unique PREFIX-N.
    const catalogue = [
      ...deps.products,
      ...mintedInBatch.map(code => ({ code } as Product)),
    ];
    const code = mintInternalCode(catalogue, deps.internalCodePrefix);
    mintedInBatch.push(code);
    deps.addProduct({
      code,
      description: line.description,
      category: 'Uncategorised',
      primarySupplier: deps.supplierId,
      purchasePrice: line.unitPrice ?? 0,
      markup: 45,
      stock: 0,
      minStock: 0,
    });
    return { internalCode: code, lineType: 'product' };
  }
  const sc = (line.supplierCode || '').trim();
  const fallback = res.internalCode || res.suggestedInternal
    || (sc ? autoMatchInternalCode({
      supplierCode: sc,
      description: line.description,
      supplierName: deps.supplierName,
      products: deps.products,
      priceBookEntry: deps.bookForSupplier.find(e => e.supplierCode.toUpperCase() === sc.toUpperCase()),
      remembered: deps.lookupSupplierCode(sc),
    })?.internalCode : '')
    || '';
  return { internalCode: fallback, lineType: 'product' };
}

export function countPreviewNewLines(
  lines: ParsedImportLine[],
  resolutions: Record<number, ImportLineState>,
  deps: ImportPreviewDeps,
): number {
  return lines.reduce((n, l, i) => {
    const res = resolutions[i] ?? buildImportLineResolution(l, deps);
    return n + (res.status === 'new' && res.userAction === 'pending' ? 1 : 0);
  }, 0);
}

export function applyImportPreviewAction(
  lines: ParsedImportLine[],
  prev: Record<number, ImportLineState>,
  idx: number,
  action: ImportUserAction,
  deps: ImportPreviewDeps,
): Record<number, ImportLineState> {
  const base = prev[idx] ?? buildImportLineResolution(lines[idx], deps);
  if (action === 'service') {
    return { ...prev, [idx]: { ...base, status: 'service', userAction: 'service' } };
  }
  if (action === 'autoCreate') {
    return { ...prev, [idx]: { ...base, status: 'new', userAction: 'autoCreate' } };
  }
  if (action === 'confirmSuggested') {
    return { ...prev, [idx]: { ...base, userAction: 'confirmSuggested', internalCode: base.suggestedInternal } };
  }
  if (action === 'pending') {
    return { ...prev, [idx]: classifyRawImportLine(lines[idx], deps) };
  }
  return prev;
}

export function batchImportPreviewAction(
  lines: ParsedImportLine[],
  prev: Record<number, ImportLineState>,
  action: 'autoCreate' | 'service',
  deps: ImportPreviewDeps,
): Record<number, ImportLineState> {
  const next = { ...prev };
  lines.forEach((l, i) => {
    const base = next[i] ?? buildImportLineResolution(l, deps);
    if (base.status === 'new') {
      next[i] = action === 'service'
        ? { ...base, status: 'service', userAction: 'service' }
        : { ...base, userAction: 'autoCreate' };
    }
  });
  return next;
}

export function mergeAutoCreateResolutions(
  lines: ParsedImportLine[],
  prev: Record<number, ImportLineState>,
  deps: ImportPreviewDeps,
): Record<number, ImportLineState> {
  const merged: Record<number, ImportLineState> = {};
  lines.forEach((l, i) => {
    const base = prev[i] ?? buildImportLineResolution(l, deps);
    merged[i] = base.status === 'new' && base.userAction === 'pending'
      ? { ...base, userAction: 'autoCreate' }
      : base;
  });
  return merged;
}
